import { useEffect, useMemo, useRef, useState } from "react";
import { StreamdownChat } from "./slots";
import type {
  ChatUiPlanProposal,
  ChatUiPlanProposalStatus,
} from "./hooks/useChatUiActions";

interface ChatPlanProposalCardProps {
  proposal: ChatUiPlanProposal;
  onDisplayComplete?: (actionId: string) => void;
}

function statusLabel(status: ChatUiPlanProposalStatus): string {
  if (status === "approved") return "Plan approved";
  if (status === "denied") return "Plan denied";
  if (status === "expired") return "Plan expired";
  return "Plan approval required";
}

export function ChatPlanProposalCard({
  proposal,
  onDisplayComplete,
}: ChatPlanProposalCardProps) {
  const isPending = proposal.status === "pending";
  const prefersReducedMotion = useMemo(
    () =>
      typeof window !== "undefined" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches,
    [],
  );
  const [visibleMarkdown, setVisibleMarkdown] = useState(
    isPending && !prefersReducedMotion ? "" : proposal.markdown,
  );
  const completedActionRef = useRef<string | null>(null);
  const streamComplete =
    !isPending || visibleMarkdown.length >= proposal.markdown.length;
  const tone =
    proposal.status === "approved"
      ? "text-emerald-400"
      : proposal.status === "denied"
        ? "text-red-400"
        : "text-[var(--fintheon-accent)]";

  useEffect(() => {
    completedActionRef.current = null;
    if (!isPending || prefersReducedMotion) {
      setVisibleMarkdown(proposal.markdown);
      completedActionRef.current = proposal.actionId;
      onDisplayComplete?.(proposal.actionId);
      return;
    }

    let index = 0;
    setVisibleMarkdown("");
    const interval = window.setInterval(() => {
      index = Math.min(proposal.markdown.length, index + 88);
      setVisibleMarkdown(proposal.markdown.slice(0, index));
      if (index < proposal.markdown.length) return;
      window.clearInterval(interval);
      if (completedActionRef.current === proposal.actionId) return;
      completedActionRef.current = proposal.actionId;
      onDisplayComplete?.(proposal.actionId);
    }, 22);

    return () => window.clearInterval(interval);
  }, [
    isPending,
    onDisplayComplete,
    prefersReducedMotion,
    proposal.actionId,
    proposal.markdown,
  ]);

  return (
    <div
      className="fintheon-plan-proposal-card w-full rounded-lg border border-[var(--fintheon-accent)]/18 bg-[#0a0905] px-3 py-3"
      data-plan-proposal-id={proposal.actionId}
      data-streaming={isPending && !streamComplete ? "true" : "false"}
    >
      <div className="mb-2 flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p
            className={`text-[10px] font-semibold uppercase tracking-[0.12em] ${tone}`}
          >
            {statusLabel(proposal.status)}
          </p>
          <h3 className="mt-1 truncate text-[13px] font-semibold text-[var(--fintheon-text)]">
            {proposal.title}
          </h3>
          {proposal.summary ? (
            <p className="mt-1 text-[11px] leading-relaxed text-zinc-500">
              {proposal.summary}
            </p>
          ) : null}
        </div>
        <span className="rounded border border-[var(--fintheon-accent)]/18 px-2 py-1 text-[10px] uppercase tracking-[0.12em] text-[var(--fintheon-accent)]/70">
          {proposal.surface === "narrativeflow" ? "NarrativeFlow" : "Chat"}
        </span>
      </div>

      <div className="fintheon-plan-proposal-card__content fintheon-chat-markdown pr-2 text-[12px] leading-relaxed text-[var(--fintheon-text)]/84">
        <StreamdownChat content={visibleMarkdown} />
      </div>

      {isPending ? (
        <p className="mt-3 border-t border-[var(--fintheon-accent)]/12 pt-2 text-[10px] font-semibold uppercase tracking-[0.12em] text-[var(--fintheon-accent)]/62">
          {streamComplete
            ? "Answer the implementation question in the composer modal below."
            : "Streaming plan..."}
        </p>
      ) : proposal.feedback ? (
        <p className="mt-3 rounded border border-[var(--fintheon-accent)]/12 bg-black/20 px-2 py-1.5 text-[11px] leading-relaxed text-[var(--fintheon-text)]/70">
          {proposal.feedback}
        </p>
      ) : null}
    </div>
  );
}
